import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container, Typography, TextField, Button, CircularProgress, Alert,
    Box, Paper, Snackbar, IconButton, List, ListItem, ListItemText, Divider
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'; // Import the icon
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EditIcon from '@mui/icons-material/Edit'; // Optional for inline editing later
import CodeIcon from '@mui/icons-material/Code';
import { BusinessContext } from '../../context/BusinessContext'; // Import BusinessContext
import { useContext } from 'react'; // Import useContext
import { spacing } from '../../utils/themeUtils';
import { isDemoUser } from '../../utils/demoUtils';

const API_URL = process.env.REACT_APP_API_URL; // Use environment variable

// Helper to convert form state array to the object structure needed by API
const formCategoriesToObject = (formCats) => {
    return formCats.reduce((acc, cat) => {
        // Ensure category name is not empty and subjects is an array
        if (cat.name.trim() && Array.isArray(cat.subjects)) {
            // Filter out empty subjects
            const validSubjects = cat.subjects.map(s => s.trim()).filter(s => s !== '');
            if (validSubjects.length > 0) {
                acc[cat.name.trim()] = validSubjects;
            }
        }
        return acc;
    }, {});
};

// Helper to convert API object structure to form state array
const objectToFormCategories = (apiObject) => {
    if (!apiObject || typeof apiObject !== 'object') {
        return [];
    }
    return Object.entries(apiObject).map(([name, subjects], index) => ({
        id: `cat-${index}-${Date.now()}-${Math.random()}`, // Include index for stability
        name: name,
        // Ensure subjects is an array of strings
        subjects: Array.isArray(subjects) ? subjects.map(String) : []
    }));
};

function CustomCategoriesSettings() {
    const [categoriesJson, setCategoriesJson] = useState('{}'); // Keep JSON string state
    const [formCategories, setFormCategories] = useState([]); // New state for form UI
    const [initialJson, setInitialJson] = useState('{}');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [validationError, setValidationError] = useState(null); // Specific for form validation
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [runAnalysisLoading, setRunAnalysisLoading] = useState(false);
    const [runAnalysisMessage, setRunAnalysisMessage] = useState('');
    const [runAnalysisError, setRunAnalysisError] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [showJsonEditor, setShowJsonEditor] = useState(false); // State to toggle JSON editor visibility
    const [userRole, setUserRole] = useState(null); // State to store user role

    // Refs for managing focus
    const categoryInputRefs = useRef({});
    const subjectInputRefs = useRef({});

    // Get selected businesses from context
    const { selectedBusinesses, userEmail } = useContext(BusinessContext);
    const numSelected = selectedBusinesses?.length || 0;
    const isDemo = isDemoUser(userEmail);

    // Fetch user role
    useEffect(() => {
        const fetchUserRole = async () => {
            try {
                const response = await fetch(`${API_URL}/users/`, { credentials: 'include' });
                if (response.ok) {
                    const userData = await response.json();
                    setUserRole(userData.role);
                }
            } catch (err) {
                console.error('Error fetching user role:', err);
                setUserRole(null);
            }
        };
        fetchUserRole();
    }, []);

    // Fetch Categories
    const fetchCategories = useCallback(async () => {
        // Fetch if one or more businesses are selected.
        // Categories of the first selected business will be used as the template.
        if (numSelected === 0) {
            setError("Select one or more businesses to manage their categories.");
            setFormCategories([]);
            setCategoriesJson('{}');
            setInitialJson('{}');
            setIsLoading(false);
            return;
        }
        
        const representativeBusinessId = selectedBusinesses[0].id; // Use first selected business's categories as template

        setIsLoading(true);
        setError(null);
        setValidationError(null);
        try {
            const url = `${API_URL}/user-business-association/categories?business_id=${representativeBusinessId}`;
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) {
                 const errorData = await response.json().catch(() => ({ detail: response.statusText }));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }
            const data = await response.json() || {};
            const initialFormCats = objectToFormCategories(data);
            setFormCategories(initialFormCats);
            const jsonString = JSON.stringify(formCategoriesToObject(initialFormCats), null, 2);
            setCategoriesJson(jsonString);
            setInitialJson(jsonString);
        } catch (err) {
            console.error("Error fetching categories:", err);
            setError(err.message || "Failed to load custom categories.");
            setCategoriesJson('{}');
            setInitialJson('{}');
            setFormCategories([]);
        } finally {
            setIsLoading(false);
        }
    }, [numSelected, selectedBusinesses]); // Depend on numSelected and selectedBusinesses

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Sync Form State -> JSON State
    useEffect(() => {
        try {
            const newJsonObject = formCategoriesToObject(formCategories);
            const newJsonString = JSON.stringify(newJsonObject, null, 2);
            setCategoriesJson(newJsonString);
            setValidationError(null); // Clear validation error if conversion is successful
        } catch (e) {
            // This should ideally not happen if form state is managed correctly
            console.error("Error converting form state to JSON:", e);
            setValidationError("Internal error converting form data.");
        }
    }, [formCategories]);

    // --- Form Handlers ---
    const handleAddCategory = () => {
        setFormCategories(prev => [
            ...prev,
            { id: `cat-${Date.now()}-${Math.random()}`, name: 'New Category', subjects: ['New Subject'] }
        ]);
         setError(null); // Clear general errors
         setValidationError(null); // Clear validation errors
    };

    const handleRemoveCategory = (idToRemove) => {
        setFormCategories(prev => prev.filter(cat => cat.id !== idToRemove));
    };

    const handleCategoryNameChange = (idToUpdate, newName) => {
        setFormCategories(prev => prev.map(cat =>
            cat.id === idToUpdate ? { ...cat, name: newName } : cat
        ));
        setError(null);
        setValidationError(null);
    };

    const handleAddSubject = (categoryId) => {
        setFormCategories(prev => prev.map(cat =>
            cat.id === categoryId ? { ...cat, subjects: [...cat.subjects, 'New Subject'] } : cat
        ));
    };

    const handleRemoveSubject = (categoryId, subjectIndexToRemove) => {
        setFormCategories(prev => prev.map(cat =>
            cat.id === categoryId
                ? { ...cat, subjects: cat.subjects.filter((_, index) => index !== subjectIndexToRemove) }
                : cat
        ));
    };

    const handleSubjectNameChange = (categoryId, subjectIndexToUpdate, newSubjectName) => {
        setFormCategories(prev => prev.map(cat =>
            cat.id === categoryId
                ? { ...cat, subjects: cat.subjects.map((subj, index) => index === subjectIndexToUpdate ? newSubjectName : subj) }
                : cat
        ));
         setError(null);
        setValidationError(null);
    };
     // --- End Form Handlers ---

    // Handle JSON Text Field Change (Manual Edit)
    const handleJsonChange = (event) => {
        const newJsonString = event.target.value;
        setCategoriesJson(newJsonString);
        // Attempt to parse and update form state if JSON is valid, otherwise show error
        try {
            const parsed = JSON.parse(newJsonString);
             if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
               throw new Error("Input must be a valid JSON object.");
            }
            setFormCategories(objectToFormCategories(parsed)); // Sync JSON -> Form if valid
            setError(null); // Clear general error
            setValidationError(null); // Clear validation error
        } catch (parseError) {
            console.error("JSON Parse Error on manual edit:", parseError);
            // Don't clear the text field, let user fix it
            setError(`Invalid JSON: ${parseError.message}`);
             setValidationError(null); // Clear specific validation error
        }
    };

    // Save Handler (uses categoriesJson state)
    const handleSave = async () => {
        // Demo users can't save changes
        if (isDemo) {
            setError("Demo mode - changes cannot be saved.");
            setSnackbarOpen(true);
            return;
        }

        // Check if one or more businesses are selected
        if (numSelected === 0) {
            setError("Select one or more businesses to save categories.");
            return;
        }
        
        let parsedCategories;
        try {
            // Use the JSON state which is derived from the form state
            parsedCategories = JSON.parse(categoriesJson);
            // Basic validation (already done during sync, but good safety check)
            if (typeof parsedCategories !== 'object' || parsedCategories === null || Array.isArray(parsedCategories)) {
               throw new Error("Cannot save invalid format. Must be a JSON object.");
            }
            setError(null);
            setValidationError(null);
        } catch (parseError) {
            console.error("JSON Parse Error on save:", parseError);
            setError(`Invalid JSON: ${parseError.message}`);
            return;
        }

        // Check for empty categories or subjects before saving
        const finalObject = formCategoriesToObject(formCategories);
        if (Object.keys(finalObject).length === 0 && formCategories.length > 0) {
            setValidationError("Cannot save with empty category names or categories with only empty subjects.");
            return;
        }
         if (Object.values(finalObject).some(subjects => subjects.length === 0)) {
             setValidationError("All categories must have at least one non-empty subject.");
            return;
        }

        setIsSaving(true);
        setError(null);
        setValidationError(null);
        setSaveSuccess(false);

        const results = [];
        let overallSuccess = true;

        for (const business of selectedBusinesses) {
            try {
                const url = `${API_URL}/user-business-association/categories?business_id=${business.id}`;
                const response = await fetch(url, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(finalObject) // Send the cleaned object
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
                    throw new Error(`Business ${business.name || business.id}: ${errorData.detail || `HTTP error! status: ${response.status}`}`);
                }
                results.push({ name: business.name || business.id, status: 'success' });
            } catch (err) {
                console.error(`Error saving categories for business ${business.id}:`, err);
                results.push({ name: business.name || business.id, status: 'error', message: err.message });
                overallSuccess = false;
            }
        }
        
        if (overallSuccess) {
            setSaveSuccess(true);
            setInitialJson(JSON.stringify(finalObject, null, 2));
            setCategoriesJson(JSON.stringify(finalObject, null, 2));
            setFormCategories(objectToFormCategories(finalObject));
            setError(null); // Clear previous general errors
        } else {
            const errorMessages = results.filter(r => r.status === 'error').map(r => r.message).join('; ');
            setError(`Failed to save categories for some businesses: ${errorMessages}`);
        }
        setSnackbarOpen(true); // Show snackbar with success or aggregated error

    };

    // Run Custom Analysis Handler
     const handleRunCustomAnalysis = async () => {
        // Demo users can't run analysis
        if (isDemo) {
            setRunAnalysisError("Demo mode - analysis cannot be run.");
            setSnackbarOpen(true);
            return;
        }

        // Check if one or more businesses are selected
        if (numSelected === 0) {
            const msg = "Select one or more businesses to run analysis.";
            setError(msg);
            setRunAnalysisError(msg);
            setSnackbarOpen(true);
            return;
        }

        setRunAnalysisLoading(true);
        setRunAnalysisMessage('');
        setRunAnalysisError('');
        
        const results = [];
        let allAnalysesStarted = true;
        let someAnalysesStarted = false;

        for (const business of selectedBusinesses) {
            try {
                const url = `${API_URL}/analysis/run-custom?business_id=${business.id}`;
                const response = await fetch(url, {
                    method: 'POST',
                    credentials: 'include'
                });
                 if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
                    throw new Error(`Business ${business.name || business.id}: ${errorData.detail || `HTTP error! status: ${response.status}`}`);
                }
                const responseData = await response.json();
                results.push({ name: business.name || business.id, status: 'success', message: responseData.message || `Analysis started for ${business.name || business.id}.` });
                someAnalysesStarted = true;
            } catch (err) {
                console.error(`Error starting analysis for business ${business.id}:`, err);
                results.push({ name: business.name || business.id, status: 'error', message: err.message });
                allAnalysesStarted = false;
            }
        }

        if (allAnalysesStarted) {
            setRunAnalysisMessage(`Analysis started successfully for all ${numSelected} selected businesses.`);
        } else if (someAnalysesStarted) {
            const successMessages = results.filter(r => r.status === 'success').map(r => r.message).join('; ');
            const errorMessages = results.filter(r => r.status === 'error').map(r => r.message).join('; ');
            setRunAnalysisMessage(`Partial success: ${successMessages}`);
            setRunAnalysisError(`Failures: ${errorMessages}`);
        } else {
            const errorMessages = results.filter(r => r.status === 'error').map(r => r.message).join('; ');
            setRunAnalysisError(`Failed to start analysis for all selected businesses. Errors: ${errorMessages}`);
        }
        setSnackbarOpen(true);
        setRunAnalysisLoading(false);
    };

    // Snackbar Close Handler
     const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
         setSaveSuccess(false);
         setRunAnalysisMessage('');
         setRunAnalysisError('');
        // Don't clear general error or validation error here, let user see them
    };

    const hasChanges = categoriesJson !== initialJson;
    const commonPaperSx = {
        p: { xs: 2, md: 3 },
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
        borderRadius: { xs: '12px', sm: '16px', md: '24px' },
        border: '1px solid rgba(25, 118, 210, 0.1)',
        boxShadow: '0 10px 20px -6px rgba(0,0,0,0.08)',
        position: 'relative',
        overflow: 'hidden', // Keep hidden for individual papers
        height: '100%', // Make papers fill grid item height
        width: '100%', // Explicitly set width to fill Grid item
        display: 'flex', // Use flexbox for vertical layout
        flexDirection: 'column',
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, #1976d2, #42a5f5)' }
    };

    // --- Enter Key Navigation and Adding --- 
    const handleCategoryKeyDown = (event, categoryId, catIndex) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            // Focus first subject field in the current category (or placeholder if no subjects)
            const firstSubjectId = `subject-input-${catIndex}-0`;
            const placeholderSubjectId = `subject-input-placeholder-${catIndex}`;
            const targetElement = document.getElementById(firstSubjectId) || document.getElementById(placeholderSubjectId);
            targetElement?.focus();
        }
    };

     const handleAddCategoryKeyDown = (event) => {
         if (event.key === 'Enter' && event.target.value.trim() !== '') {
             event.preventDefault();
             const newCategoryName = event.target.value.trim();
             const newCategoryId = `cat-${formCategories.length}-${Date.now()}-${Math.random()}`;
             // Add the new category with an empty subject list 
             setFormCategories(prev => [
                 ...prev,
                 { id: newCategoryId, name: newCategoryName, subjects: [] }
             ]);
             // Focus the new category name field shortly after state update
             setTimeout(() => {
                 document.getElementById(`category-input-${formCategories.length}`)?.focus();
             }, 0);
             event.target.value = ''; // Clear the placeholder input
         }
     };

    // Keydown for EXISTING subject fields
    const handleSubjectKeyDown = (event, categoryId, catIndex, subIndex) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            const currentCategory = formCategories.find(c => c.id === categoryId);
            if (!currentCategory) return;

            const isLastSubject = subIndex === currentCategory.subjects.length - 1;

            if (!isLastSubject) {
                // Move focus to the next subject field within the same category
                const nextSubjectId = `subject-input-${catIndex}-${subIndex + 1}`;
                document.getElementById(nextSubjectId)?.focus();
            } else {
                // If Enter on the last actual subject, move focus to the ADD subject placeholder for this category
                const addSubjectPlaceholderId = `subject-input-placeholder-${catIndex}`;
                document.getElementById(addSubjectPlaceholderId)?.focus();
            }
        }
    };

    // Keydown for the ADD subject placeholder field
    const handleAddSubjectKeyDown = (event, categoryId, catIndex) => {
        if (event.key === 'Enter') {
             event.preventDefault();
             const newSubjectName = event.target.value.trim();

            if (newSubjectName !== '') {
                // Add the new subject to the state
                setFormCategories(prev => prev.map(cat =>
                    cat.id === categoryId ? { ...cat, subjects: [...cat.subjects, newSubjectName] } : cat
                ));
                // Clear the placeholder input and keep focus on it
                event.target.value = '';
                // Ensure focus stays or returns shortly after state update if needed
                // setTimeout(() => document.getElementById(event.target.id)?.focus(), 0); 
            } else {
                // If Enter is pressed in an empty placeholder, move to next category's placeholder
                 const nextCategoryPlaceholderId = `category-input-${formCategories.length}`;
                 document.getElementById(nextCategoryPlaceholderId)?.focus();
             }
        }
    };

    // --- Blur Handlers for Cleanup --- 
    const handlePlaceholderCategoryBlur = (event) => { /* No action needed */ };
    // No blur handler needed for subject placeholder anymore

    return (
        <Box sx={{
            minHeight: '100%',
            background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
            display: 'flex',
            flexDirection: 'column',
            px: spacing.pageHorizontal,
            pt: spacing.pageTop,
            pb: spacing.pageBottom,
            opacity: 0,
            animation: 'fadeIn 1s forwards',
            '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 },
            },
            width: '100%' // Ensure this Box takes full width available to it
        }}>
            <Container maxWidth={false} sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: { xs: 2, lg: 4 } }}>
                <Box sx={{
                    mb: { xs: 2, lg: 4 },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, md: 3 },
                    alignItems: { xs: 'flex-start', md: 'flex-start' }, // Align items to start like Reviews.js
                    width: '100%',
                    height: { xs: 'auto', md: 'auto' } // Use auto height unless fixed height is needed
                }}>
                    {/* First Column: Title and Description */}
                    <Box sx={{
                        flex: { xs: '1 1 100%', md: '1 1 33.33%' },
                        px: { xs: 0, md: 2 },
                        height: { xs: 'auto', md: '100%' } // Use full height
                    }}>
                        <Typography 
                            variant="h3" 
                            component="h1" 
                            sx={{ 
                                fontWeight: 800, 
                                fontSize: { xs: '1.75rem', lg: '2.5rem' }, 
                                color: '#0A2540', 
                                letterSpacing: '-0.02em',
                                mb: { xs: 1, lg: 2 }, // Match Reviews.js margin
                                display: 'flex', // Make flex to align icon
                                alignItems: 'center',
                                gap: { xs: 1, lg: 2 } // Match Reviews.js gap
                            }}
                        >
                            <PlaylistAddCheckIcon sx={{ fontSize: 'inherit', color: '#1976d2' }} /> {/* Match icon styling */}
                            Categories
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: '#546E7A', 
                                fontSize: { xs: '0.95rem', lg: '1.1rem' } // Match Reviews.js font size
                            }}
                        >
                            Define categories and their subjects.
                        </Typography>
                     </Box>

                     {/* Second Column: Helper Text */}
                     <Box sx={{
                        flex: { xs: '1 1 100%', md: '1 1 33.33%' },
                        px: spacing.headerHorizontal,
                        height: { xs: 'auto', md: '100%' }, // Take full height
                        display: 'flex',
                        alignItems: 'flex-end' // Align text to bottom if needed, or adjust as required
                     }} >
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'left' }}>
                           Group products/services by category (e.g., Desserts: Ice Cream, Cake, Pie) to organize insights.
                        </Typography>
                     </Box>

                     {/* Third Column: Toggle Button - Hidden on mobile and for non-admin users */}
                     {userRole === 'admin' && (
                     <Box sx={{ 
                        flex: { xs: '1 1 100%', md: '1 1 33.33%' }, 
                        px: spacing.headerHorizontal, 
                        height: { xs: 'auto', md: '100%' }, // Take full height
                        display: { xs: 'none', md: 'flex' }, // Hide on mobile
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end'
                     }}>
                         <Button
                            variant="outlined"
                            startIcon={<CodeIcon />}
                            onClick={() => setShowJsonEditor(!showJsonEditor)}
                            sx={{ 
                                color: '#1976d2',
                                borderColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    borderColor: '#1976d2'
                                }
                            }}
                        >
                            {showJsonEditor ? 'Hide JSON Editor' : 'Show JSON Editor'}
                        </Button>
                     </Box>
                     )}
                </Box>

                {error && !isSaving && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}
                {validationError && !isSaving && <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>{validationError}</Alert>}

                <Box sx={{ flexGrow: 1, display: 'flex', minHeight: 0, width: '100%' }}>
                    <Grid container spacing={3} sx={{ width: '100%' }}>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Paper sx={commonPaperSx}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#0A2540', pt: '4px' }}>
                                    Edit Categories
                                </Typography>
                                {/* Disable editing UI if not exactly one business selected or if demo user */} 
                                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1, opacity: (numSelected > 0 && !isDemo) ? 1 : 0.5, pointerEvents: (numSelected > 0 && !isDemo) ? 'auto' : 'none' }}>
                                    <List dense disablePadding>
                                        {formCategories.map((category, catIndex) => (
                                            <Paper key={category.id} variant="outlined" sx={{ mb: 2, p: 1.5, background: 'rgba(255,255,255,0.6)' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <TextField
                                                        id={`category-input-${catIndex}`}
                                                        fullWidth variant="standard" size="small"
                                                        value={category.name}
                                                        onChange={(e) => handleCategoryNameChange(category.id, e.target.value)}
                                                        onKeyDown={(e) => handleCategoryKeyDown(e, category.id, catIndex)}
                                                        placeholder="Category Name"
                                                        sx={{ mr: 1, '.MuiInput-input': { fontWeight: 'bold' } }}
                                                        error={!category.name.trim()}
                                                    />
                                                    <IconButton onClick={() => handleRemoveCategory(category.id)} size="small" color="error" title="Remove Category">
                                                        <RemoveCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <List dense disablePadding sx={{ pl: 2 }}>
                                                    {category.subjects.map((subject, subIndex) => (
                                                        <ListItem key={`${category.id}-subj-${subIndex}`} disableGutters>
                                                            <TextField
                                                                id={`subject-input-${catIndex}-${subIndex}`}
                                                                fullWidth variant="standard" size="small"
                                                                value={subject}
                                                                onChange={(e) => handleSubjectNameChange(category.id, subIndex, e.target.value)}
                                                                onKeyDown={(e) => handleSubjectKeyDown(e, category.id, catIndex, subIndex)}
                                                                placeholder="Subject Name"
                                                                sx={{ mr: 1 }}
                                                                error={!subject.trim()}
                                                            />
                                                            {/* Remove button for all actual subjects */} 
                                                            <IconButton onClick={() => handleRemoveSubject(category.id, subIndex)} size="small" color="error" title="Remove Subject">
                                                                <RemoveCircleOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </ListItem>
                                                    ))}
                                                    {/* Dedicated Placeholder for adding a new subject */} 
                                                    <ListItem disableGutters>
                                                        <TextField
                                                            id={`subject-input-placeholder-${catIndex}`}
                                                            fullWidth
                                                            variant="standard"
                                                            size="small"
                                                            placeholder="Add New Subject... (Press Enter)"
                                                            onKeyDown={(e) => handleAddSubjectKeyDown(e, category.id, catIndex)}
                                                            sx={{ mr: 1, '.MuiInput-input': { fontStyle: 'italic' } }}
                                                        />
                                                         {/* No remove button for placeholder */}
                                                    </ListItem>
                                                </List>
                                            </Paper>
                                        ))}
                                        {/* Placeholder for adding a new category */} 
                                        <Paper variant="outlined" sx={{ mb: 2, p: 1.5, background: 'rgba(245,245,255,0.4)', borderStyle: 'dashed' }}>
                                            <TextField
                                                id={`category-input-${formCategories.length}`}
                                                fullWidth
                                                variant="standard"
                                                size="small"
                                                placeholder="Add New Category... (Press Enter)"
                                                onKeyDown={handleAddCategoryKeyDown}
                                                onBlur={handlePlaceholderCategoryBlur}
                                                sx={{ '.MuiInput-input': { fontStyle: 'italic' } }}
                                            />
                                        </Paper>
                                    </List>
                                </Box>
                            </Paper>
                            
                            {/* Mobile JSON Toggle Button - Only shown on mobile and for admin users */}
                            {userRole === 'admin' && (
                            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<CodeIcon />}
                                    onClick={() => setShowJsonEditor(!showJsonEditor)}
                                    sx={{ 
                                        color: '#1976d2',
                                        borderColor: '#1976d2',
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                            borderColor: '#1976d2'
                                        }
                                    }}
                                >
                                    {showJsonEditor ? 'Hide JSON Editor' : 'Show JSON Editor'}
                                </Button>
                            </Box>
                            )}
                        </Grid>

                        {showJsonEditor && userRole === 'admin' && (
                            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                                <Paper sx={commonPaperSx}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#0A2540', pt: '4px' }}>
                                        Edit as JSON
                                    </Typography>
                                    {/* Disable editing UI if not exactly one business selected or if demo user */} 
                                    <TextField
                                        label="Custom Categories (JSON)"
                                        multiline
                                        value={categoriesJson}
                                        onChange={handleJsonChange}
                                        variant="outlined"
                                        fullWidth
                                        error={!!error && error.startsWith('Invalid JSON')}
                                        disabled={numSelected === 0 || isDemo} // Disable if no businesses selected or demo user
                                        sx={{ 
                                            fontFamily: 'monospace', 
                                            flexGrow: 1, 
                                            '& .MuiInputBase-root': { height: '100%' }, 
                                            '& .MuiInputBase-inputMultiline': { height: '100% !important', overflowY: 'auto !important' },
                                            opacity: (numSelected > 0 && !isDemo) ? 1 : 0.5 // Visual cue
                                        }}
                                    />
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                {/* Show message if not exactly one selected or demo mode */} 
                {numSelected === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Select one or more businesses from the 'Businesses' page to manage their categories.
                    </Alert>
                ) : isDemo ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Demo mode - Categories are read-only.
                    </Alert>
                ) : null}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 2, flexShrink: 0 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                disabled={isSaving || isLoading || !hasChanges || !!validationError || !!error || numSelected === 0 || isDemo}
                            >
                                {isSaving ? <CircularProgress size={24} /> : isDemo ? "Demo - Read Only" : `Save for ${numSelected} Business${numSelected !== 1 ? 'es' : ''}`}
                            </Button>
                            {userRole === 'admin' && !isDemo && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleRunCustomAnalysis}
                                    disabled={runAnalysisLoading || isLoading || hasChanges || numSelected === 0}
                                    title={numSelected === 0 ? "Select one or more businesses first" : hasChanges ? "Save changes before running analysis" : `Run analysis for ${numSelected} selected business${numSelected !== 1 ? 'es' : ''}`}
                                >
                                    {runAnalysisLoading ? <CircularProgress size={24} /> : `Run Analysis for ${numSelected} Business${numSelected !== 1 ? 'es' : ''}`}
                                </Button>
                            )}
                         </Box>
                {hasChanges && <Alert severity="warning" sx={{ mt: 2, flexShrink: 0 }}>You have unsaved changes.</Alert>}
            </Container>
             <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                 <Alert
                    onClose={handleSnackbarClose}
                    severity={saveSuccess ? "success" : runAnalysisMessage && !runAnalysisError ? "info" : runAnalysisError ? "error" : error ? "error" : validationError ? "warning" : "info"}
                    sx={{ width: '100%' }}
                >
                    {saveSuccess ? `Categories saved successfully for ${numSelected} business${numSelected !==1 ? 'es' : ''}!` 
                                  : runAnalysisMessage ? runAnalysisMessage 
                                  : runAnalysisError ? runAnalysisError 
                                  : error ? error 
                                  : validationError ? validationError : ""}
                 </Alert>
            </Snackbar>
        </Box>
    );
}

export default CustomCategoriesSettings;

